// WorksPage.jsx - Updated with unique CSS class names
import React, { useState, useEffect, useRef } from "react";
import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  list,
} from "firebase/storage";
import { initializeApp } from "firebase/app";
import "./WorksPage.css";

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAYzoT7Gmkd853xwGX16tUQhlkdIrKvHUs",
  authDomain: "photo-gallery-68f56.firebaseapp.com",
  projectId: "photo-gallery-68f56",
  storageBucket: "photo-gallery-68f56.firebasestorage.app",
  messagingSenderId: "360109720638",
  appId: "1:360109720638:web:17143a0ab7ad444428fd6f",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

const WorksPage = () => {
  const [categories, setCategories] = useState([
    { id: "latest", name: "LATEST", path: "works/latest/", active: true },
    {
      id: "editorial",
      name: "EDITORIAL",
      path: "works/editorial/",
      active: false,
    },
    {
      id: "commercial",
      name: "COMMERCIAL",
      path: "works/commercial/",
      active: false,
    },
    {
      id: "portfolios",
      name: "WEDDING",
      path: "works/Wedding/",
      active: false,
    },
  ]);

  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumImages, setAlbumImages] = useState([]);
  const [viewMode, setViewMode] = useState("albums");
  const [selectedCategory, setSelectedCategory] = useState("latest");
  const [masonryColumns, setMasonryColumns] = useState(4);
  const [albumMasonryColumns, setAlbumMasonryColumns] = useState(3);

  // Full-screen viewer state
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentAlbumImages, setCurrentAlbumImages] = useState([]);

  const masonryRef = useRef(null);

  // Handle window resize for responsive masonry columns
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      // For albums view masonry grid
      if (width < 640) {
        setAlbumMasonryColumns(1);
      } else if (width < 1024) {
        setAlbumMasonryColumns(2);
      } else {
        setAlbumMasonryColumns(3);
      }

      // For album detail view masonry grid
      if (width < 768) {
        setMasonryColumns(1);
      } else if (width < 1024) {
        setMasonryColumns(2);
      } else if (width < 1440) {
        setMasonryColumns(3);
      } else {
        setMasonryColumns(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    // Handle keyboard navigation for full-screen viewer
    const handleKeyDown = (e) => {
      if (!isFullScreen) return;

      if (e.key === "Escape") {
        closeFullScreen();
      } else if (e.key === "ArrowLeft") {
        navigateImage(-1);
      } else if (e.key === "ArrowRight") {
        navigateImage(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullScreen]);

  const handleCategorySelect = async (categoryId) => {
    const updatedCategories = categories.map((cat) => ({
      ...cat,
      active: cat.id === categoryId,
    }));
    setCategories(updatedCategories);
    setSelectedCategory(categoryId);
    await fetchAlbums(categoryId);
  };

  const fetchAlbums = async (categoryId = "latest") => {
    setLoading(true);
    setViewMode("albums");
    setSelectedAlbum(null);
    setAlbumImages([]);
    setIsFullScreen(false);

    try {
      const category = categories.find((cat) => cat.id === categoryId);
      if (!category) return;

      const categoryRef = ref(storage, category.path);
      const result = await list(categoryRef);

      const albumPromises = result.prefixes.map(async (folderRef) => {
        const folderName = folderRef.name;

        let coverImageUrl = null;
        let imageCount = 0;
        let allImageUrls = [];

        try {
          const imagesResult = await listAll(folderRef);
          imageCount = imagesResult.items.length;

          // Get all image URLs for the album
          const imageUrlPromises = imagesResult.items.map(async (item) => {
            return await getDownloadURL(item);
          });

          allImageUrls = await Promise.all(imageUrlPromises);

          if (allImageUrls.length > 0) {
            coverImageUrl = allImageUrls[0];
          }
        } catch (error) {
          console.warn(
            `Could not fetch images for album ${folderName}:`,
            error
          );
        }

        // Generate random aspect ratio for masonry effect
        const aspectRatios = [0.75, 0.85, 1, 1.1, 1.25];
        const randomAspect =
          aspectRatios[Math.floor(Math.random() * aspectRatios.length)];

        return {
          id: `${categoryId}-${folderName}`,
          name: formatAlbumName(folderName),
          folderName: folderName,
          coverImage:
            coverImageUrl || getRandomUnsplashImage(categoryId, folderName),
          imageCount: imageCount,
          allImages: allImageUrls,
          path: folderRef.fullPath,
          category: category.name,
          categoryId: categoryId,
          aspectRatio: randomAspect,
          isSample: !coverImageUrl,
        };
      });

      const albumsList = await Promise.all(albumPromises);
      await new Promise((resolve) => setTimeout(resolve, 300));
      setAlbums(albumsList);
    } catch (error) {
      console.error("Error fetching albums:", error);
      setAlbums(generateSampleAlbums(categoryId));
    } finally {
      setLoading(false);
    }
  };

  const getRandomUnsplashImage = (categoryId, folderName) => {
    const categoryThemes = {
      latest: "photography,art,creative",
      editorial: "magazine,fashion,editorial",
      commercial: "advertising,product,brand",
      portfolios: "wedding,bride,ceremony",
    };

    const theme = categoryThemes[categoryId] || "photography";
    const searchTerm = `${theme},${folderName
      .toLowerCase()
      .replace(/ /g, ",")}`;
    return `https://source.unsplash.com/random/400x300/?${searchTerm}`;
  };

  // Open full-screen viewer when thumbnail is clicked
  const openFullScreenViewer = (album, imageIndex = 0) => {
    setSelectedAlbum(album);

    // If album has allImages (from Firebase), use those
    // Otherwise, fetch images for the album
    if (album.allImages && album.allImages.length > 0) {
      const images = album.allImages.map((url, index) => ({
        id: `${album.id}-${index}`,
        url: url,
        title: `${album.name} - Image ${index + 1}`,
        filename: `image-${index + 1}.jpg`,
        album: album.name,
        category: album.category,
      }));

      setCurrentAlbumImages(images);
      setCurrentImageIndex(imageIndex);
      setIsFullScreen(true);
    } else {
      // Fetch images if not already loaded
      fetchAlbumImagesForViewer(album, imageIndex);
    }
  };

  const fetchAlbumImagesForViewer = async (album, startIndex = 0) => {
    try {
      const albumRef = ref(storage, album.path);
      const result = await listAll(albumRef);

      const imagePromises = result.items.map(async (item, index) => {
        const url = await getDownloadURL(item);
        return {
          id: `${album.id}-${index}`,
          url: url,
          title: `${album.name} - Image ${index + 1}`,
          filename: item.name,
          album: album.name,
          category: album.category,
        };
      });

      const images = await Promise.all(imagePromises);
      setCurrentAlbumImages(images);
      setCurrentImageIndex(startIndex);
      setIsFullScreen(true);
    } catch (error) {
      console.error("Error fetching album images:", error);
      // Use sample images if Firebase fails
      const sampleImages = generateSampleImages(album.name, album.category);
      setCurrentAlbumImages(sampleImages);
      setCurrentImageIndex(startIndex);
      setIsFullScreen(true);
    }
  };

  const fetchAlbumImages = async (album) => {
    setViewMode("album-detail");
    setSelectedAlbum(album);
    setAlbumImages([]);

    try {
      const albumRef = ref(storage, album.path);
      const result = await listAll(albumRef);

      const imagePromises = result.items.map(async (item, index) => {
        const url = await getDownloadURL(item);
        return {
          id: `${album.id}-${index}`,
          url: url,
          title: `${album.name} - Image ${index + 1}`,
          filename: item.name,
          album: album.name,
          category: album.category,
        };
      });

      const images = await Promise.all(imagePromises);
      setAlbumImages(images);
    } catch (error) {
      console.error("Error fetching album images:", error);
      setAlbumImages(generateSampleImages(album.name, album.category));
    }
  };

  const formatAlbumName = (folderName) => {
    return folderName
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const generateSampleAlbums = (categoryId) => {
    const categoryNames = {
      latest: [
        "Summer Collection",
        "Urban Life",
        "Nature Walks",
        "Street Photography",
      ],
      editorial: [
        "Fashion Week",
        "Magazine Cover",
        "Style Feature",
        "Beauty Editorial",
      ],
      commercial: [
        "Product Launch",
        "Brand Campaign",
        "Advertising",
        "Corporate",
      ],
      portfolios: [
        "Bridal Portraits",
        "Wedding Ceremony",
        "Reception",
        "Couple Shots",
      ],
    };

    const names = categoryNames[categoryId] || ["Sample Album"];

    return names.map((name, index) => {
      const aspectRatios = [0.75, 0.85, 1, 1.1, 1.25];
      const randomAspect =
        aspectRatios[Math.floor(Math.random() * aspectRatios.length)];

      return {
        id: `sample-${categoryId}-${index}`,
        name: name,
        folderName: name.toLowerCase().replace(/ /g, "-"),
        coverImage: getRandomUnsplashImage(categoryId, name),
        imageCount: Math.floor(Math.random() * 20) + 5,
        allImages: [],
        path: `works/${categoryId}/${name.toLowerCase().replace(/ /g, "-")}/`,
        category: categoryId.toUpperCase(),
        categoryId: categoryId,
        aspectRatio: randomAspect,
        isSample: true,
      };
    });
  };

  const generateSampleImages = (albumName, category) => {
    const images = [];
    const count = 12;

    for (let i = 1; i <= count; i++) {
      images.push({
        id: `sample-${albumName}-${i}`,
        url: `https://source.unsplash.com/random/800x600/?${category.toLowerCase()},${albumName
          .toLowerCase()
          .replace(/ /g, ",")},${i}`,
        title: `${albumName} - Image ${i}`,
        filename: `image-${i}.jpg`,
        album: albumName,
        category: category,
        aspectRatio: 0.75 + Math.random() * 0.5,
      });
    }

    return images;
  };

  // Full-screen viewer navigation functions
  const navigateImage = (direction) => {
    if (currentAlbumImages.length === 0) return;

    let newIndex = currentImageIndex + direction;

    // Loop around if at ends
    if (newIndex < 0) {
      newIndex = currentAlbumImages.length - 1;
    } else if (newIndex >= currentAlbumImages.length) {
      newIndex = 0;
    }

    setCurrentImageIndex(newIndex);
  };

  const closeFullScreen = () => {
    setIsFullScreen(false);
    setCurrentImageIndex(0);
    setCurrentAlbumImages([]);
  };

  const openImageInNewTab = () => {
    if (currentAlbumImages[currentImageIndex]) {
      window.open(currentAlbumImages[currentImageIndex].url, "_blank");
    }
  };

  // Organize albums into masonry columns
  const organizeAlbumsMasonryColumns = (albums, columns) => {
    const columnArray = Array.from({ length: columns }, () => []);

    const sortedAlbums = [...albums].sort(
      (a, b) => (b.aspectRatio || 1) - (a.aspectRatio || 1)
    );

    sortedAlbums.forEach((album, index) => {
      const columnIndex = index % columns;
      columnArray[columnIndex].push(album);
    });

    return columnArray;
  };

  // Organize images into masonry columns
  const organizeImagesMasonryColumns = (images, columns) => {
    const columnArray = Array.from({ length: columns }, () => []);

    const sortedImages = [...images].sort(
      (a, b) => (b.aspectRatio || 1) - (a.aspectRatio || 1)
    );

    sortedImages.forEach((image, index) => {
      const columnIndex = index % columns;
      columnArray[columnIndex].push(image);
    });

    return columnArray;
  };

  const backToAlbums = () => {
    setViewMode("albums");
    setSelectedAlbum(null);
    setAlbumImages([]);
    setIsFullScreen(false);
  };

  useEffect(() => {
    fetchAlbums("latest");
  }, []);

  const activeCategory = categories.find((cat) => cat.active) || categories[0];

  const albumsMasonryColumns = organizeAlbumsMasonryColumns(
    albums,
    albumMasonryColumns
  );

  const imagesMasonryColumns = organizeImagesMasonryColumns(
    albumImages,
    masonryColumns
  );

  // Full-screen Viewer Component
  const FullScreenViewer = () => {
    if (!isFullScreen || currentAlbumImages.length === 0) return null;

    const currentImage = currentAlbumImages[currentImageIndex];

    return (
      <div className="wp-full-screen-viewer">
        <div
          className="wp-full-screen-backdrop"
          onClick={closeFullScreen}
        ></div>

        <div className="wp-full-screen-content">
          {/* Close button */}
          <button className="wp-full-screen-close" onClick={closeFullScreen}>
            ×
          </button>

          {/* Navigation buttons */}
          <button
            className="wp-full-screen-nav prev"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(-1);
            }}
          >
            ‹
          </button>

          <button
            className="wp-full-screen-nav next"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(1);
            }}
          >
            ›
          </button>

          {/* Main image */}
          <div
            className="wp-full-screen-image-container"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentImage.url}
              alt={currentImage.title}
              className="wp-full-screen-image"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="wp-container">
      {/* Header Section */}
      <header className="wp-header">
        <div className="wp-header-content">
          <nav className="wp-categories-nav">
            <ul>
              {categories.map((category) => (
                <li key={category.id}>
                  <button
                    className={`wp-category-btn ${
                      category.active ? "active" : ""
                    } ${loading ? "disabled" : ""}`}
                    onClick={() =>
                      !loading && handleCategorySelect(category.id)
                    }
                    disabled={loading}
                  >
                    {category.name}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="wp-nav-indicator">
            {categories.map((category) => (
              <div
                key={`indicator-${category.id}`}
                className={`wp-indicator-dot ${
                  category.active ? "active" : ""
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      <main className="wp-content">
        {/* Albums View with Masonry Grid */}
        {viewMode === "albums" ? (
          <section className="wp-albums-section" ref={masonryRef}>
            {loading ? (
              <div className="wp-loading-container">
                <div className="wp-spinner-container">
                  <div className="wp-modern-spinner">
                    <div className="wp-spinner-circle"></div>
                    <div className="wp-spinner-text">Loading Albums...</div>
                  </div>
                </div>
              </div>
            ) : albums.length > 0 ? (
              <div className="wp-masonry-grid-container">
                <div
                  className="wp-masonry-grid"
                  style={{
                    gridTemplateColumns: `repeat(${albumMasonryColumns}, 1fr)`,
                    gap: "20px",
                  }}
                >
                  {albumsMasonryColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className="wp-masonry-column">
                      {column.map((album) => (
                        <div
                          key={album.id}
                          className="wp-album-masonry-card"
                          onClick={() => openFullScreenViewer(album, 0)}
                          style={{
                            aspectRatio: album.aspectRatio || 1,
                            minHeight: "250px",
                            cursor: "pointer",
                          }}
                        >
                          <div className="wp-album-masonry-cover">
                            <img
                              src={album.coverImage}
                              alt={album.name}
                              className="wp-album-masonry-image"
                              loading="lazy"
                            />
                            <div className="wp-album-masonry-overlay">
                              <div className="wp-album-overlay-content">
                                <h4 className="wp-album-masonry-name">
                                  {album.name}
                                </h4>
                                <div className="wp-album-image-count">
                                  <span>{album.imageCount} images</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="wp-no-albums-message">
                <div className="wp-empty-state">
                  <div className="wp-empty-icon">📁</div>
                  <h3>No Albums Found</h3>
                  <p>
                    Create folders inside <code>{activeCategory.path}</code> in
                    Firebase Storage. Each folder will appear as an album here.
                  </p>
                </div>
              </div>
            )}
          </section>
        ) : (
          /* Album Detail View */
          <section className="wp-album-detail-view" ref={masonryRef}>
            <div className="wp-album-header">
              <button className="wp-back-button" onClick={backToAlbums}>
                ← Back to {selectedAlbum?.category} Albums
              </button>
              <div className="wp-album-header-info">
                <div className="wp-album-category-header">
                  <span className="wp-category-label">
                    {selectedAlbum?.category}
                  </span>
                  <span className="wp-category-separator">/</span>
                  <h2 className="wp-album-detail-title">
                    {selectedAlbum?.name}
                  </h2>
                </div>
              </div>
            </div>

            {/* Masonry Grid Album Images */}
            {albumImages.length > 0 && (
              <div className="wp-masonry-grid-container">
                <div
                  className="wp-masonry-grid"
                  style={{
                    gridTemplateColumns: `repeat(${masonryColumns}, 1fr)`,
                    gap: "16px",
                  }}
                >
                  {imagesMasonryColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className="wp-masonry-column">
                      {column.map((image, index) => (
                        <div
                          key={image.id}
                          className="wp-masonry-item"
                          onClick={() =>
                            openFullScreenViewer(selectedAlbum, index)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <div className="wp-masonry-image-container">
                            <img
                              src={image.url}
                              alt={image.title}
                              className="wp-masonry-image"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </main>

      {/* Full-screen Viewer */}
      <FullScreenViewer />
    </div>
  );
};

export default WorksPage;
